import { FLAG_BADGES, RESULT_BADGES } from '@parlay-picker/shared/badgeConfig';
import type { BoxScorePlayerRow, GameStatus, GameTrackedLeg } from '@parlay-picker/shared/hooks';
import { getStatValue } from '@parlay-picker/shared/hooks';
import type { LeagueKey } from '@parlay-picker/shared/league';
import { shortStatLabel } from '@parlay-picker/shared/statLabels';
import { getTeamAbbr } from '@parlay-picker/shared/teams';
import { Check, X as XIcon } from 'lucide-react';
import type { ReactElement } from 'react';
import { formatStat, isBenchOrDNP, STAT_COLUMNS } from './TeamBoxScore';

/** Compact card showing a tracked player — with stats (live/final) or just name (pre-game) */
export function TrackedPlayerCard({
  player,
  playerName,
  trackedLegs = [],
  gameState: _gameState,
  isOnCourt = false,
  leagueKey,
}: {
  player?: BoxScorePlayerRow;
  playerName?: string;
  trackedLegs?: GameTrackedLeg[];
  gameState?: GameStatus;
  isOnCourt?: boolean;
  /** The league of the game this box score shows. */
  leagueKey: LeagueKey;
}): ReactElement {
  const name = player?.playerName ?? playerName ?? '';
  const hasStats = !!player;
  const isDNP = hasStats && isBenchOrDNP(player.min);
  // Get team abbreviation from first tracked leg
  const teamRaw = trackedLegs.find((l) => l.team)?.team;
  const teamAbbr = teamRaw ? getTeamAbbr(teamRaw, leagueKey) : null;

  return (
    <div className="rounded-lg bg-[var(--color-primary)]/8 border border-[var(--color-primary)]/20 overflow-hidden">
      <div className="flex items-center gap-1.5 px-3 py-1.5 flex-wrap">
        {isOnCourt && (
          <span className="inline-block w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse shrink-0" />
        )}
        <span className="text-sm font-medium text-white truncate">{name}</span>
        {teamAbbr && (
          <span className="text-[10px] text-theme-text-tertiary font-medium">{teamAbbr}</span>
        )}
        {trackedLegs.length === 0 && (
          <span
            className={`text-[9px] px-1 py-0.5 rounded font-bold shrink-0 ${FLAG_BADGES.isTracked.bg} ${FLAG_BADGES.isTracked.text}`}
          >
            {FLAG_BADGES.isTracked.label}
          </span>
        )}
        {isDNP && (
          <span
            className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${RESULT_BADGES.dnp.bg} ${RESULT_BADGES.dnp.text}`}
          >
            {RESULT_BADGES.dnp.label}
          </span>
        )}
        {/* Tracked prop pills — inline with name */}
        {trackedLegs.map((leg, i) => {
          const liveValue = player && !isDNP ? getStatValue(player, leg.statType) : null;
          const currentValue = liveValue ?? leg.actualValue;
          const isSettled = leg.hit !== null;
          const isHit = leg.hit === true;
          let isCurrentlyHitting: boolean | null = null;
          if (!isSettled && currentValue !== null && leg.line !== null) {
            isCurrentlyHitting =
              leg.overUnder === 'Over' ? currentValue > leg.line : currentValue < leg.line;
          }

          let pillBg: string;
          let pillText: string;
          if (isSettled) {
            pillBg = isHit ? 'bg-theme-success/15' : 'bg-theme-danger/15';
            pillText = isHit ? 'text-theme-success' : 'text-theme-danger';
          } else if (isCurrentlyHitting === true) {
            pillBg = 'bg-theme-success/15';
            pillText = 'text-theme-success';
          } else if (isCurrentlyHitting === false) {
            pillBg = 'bg-theme-warning/15';
            pillText = 'text-theme-warning';
          } else {
            pillBg = 'bg-[var(--color-primary)]/10';
            pillText = 'text-[var(--color-primary)]';
          }

          const statLabel = shortStatLabel(leg.statType);

          return (
            <div
              key={`${leg.statType}-${leg.line}-${i}`}
              className={`text-[10px] rounded-full font-medium px-2 py-0.5 flex items-center gap-1 ${pillBg} ${pillText}`}
            >
              {isSettled && (
                <span className="shrink-0">
                  {isHit ? (
                    <Check size={10} className="text-theme-success" />
                  ) : (
                    <XIcon size={10} className="text-theme-danger" />
                  )}
                </span>
              )}
              <span>
                {leg.overUnder ? `${leg.overUnder} ` : ''}
                {statLabel}
              </span>
              {currentValue !== null && leg.line != null ? (
                <span className="font-bold font-mono">
                  {currentValue}/{leg.line}
                </span>
              ) : leg.line != null ? (
                <span className="font-mono">{leg.line}</span>
              ) : null}
            </div>
          );
        })}
      </div>

      {/* Full-width progress bars — line at 80%, max scale = line x 1.25 (matches LegProgressBar) */}
      {trackedLegs.some((leg) => leg.hit === null && leg.line !== null && leg.line > 0) && (
        <div className="px-3 pb-1.5 flex flex-col gap-1.5">
          {trackedLegs.map((leg, i) => {
            if (leg.hit !== null || leg.line == null || leg.line <= 0) return null;
            const liveValue = player && !isDNP ? getStatValue(player, leg.statType) : null;
            const currentValue = liveValue ?? leg.actualValue;
            if (currentValue === null) return null;

            const maxScale = leg.line * 1.25;
            const linePos = 80;
            const fillPct = Math.min((currentValue / maxScale) * 100, 100);

            const isOver = leg.overUnder === 'Over';
            const isBusted = !isOver && currentValue > leg.line;
            const isAtRisk = !isOver && !isBusted && (currentValue / leg.line) * 100 >= 80;
            const isOnTrack = isOver && currentValue >= leg.line;

            let barColor = 'bg-theme-text-tertiary';
            if (isBusted) barColor = 'bg-theme-danger';
            else if (isAtRisk) barColor = 'bg-theme-warning';
            else if (isOnTrack) barColor = 'bg-theme-success';

            const statLabel = shortStatLabel(leg.statType);

            return (
              <div key={`bar-${leg.statType}-${i}`}>
                <div className="flex items-center justify-between text-[9px] text-theme-text-tertiary mb-0.5">
                  <span>
                    {leg.overUnder} {statLabel}
                  </span>
                  <span className="font-mono">
                    {currentValue} / {leg.line}
                  </span>
                </div>
                <div className="relative h-1.5 w-full rounded-full bg-theme-surface-hover overflow-hidden">
                  <div
                    className={`absolute top-0 left-0 h-full rounded-full transition-all duration-500 ${barColor}`}
                    style={{ width: `${fillPct}%` }}
                  />
                  <div
                    className="absolute top-0 w-0.5 h-full bg-white/70"
                    style={{ left: `${linePos}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {hasStats && !isDNP && (
        <div className="grid grid-cols-11 text-center text-[10px] font-mono px-1 pb-1.5 gap-0.5">
          {STAT_COLUMNS.map((col) => (
            <div key={col}>
              <div className="text-theme-text-tertiary">{col}</div>
              <div className="text-white font-medium">{formatStat(player, col)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
